import React from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { NetworkStatusItem, getNetworkStatusSeverity, getNetworkStatusLabel } from '../../../models/NetworkStatus';

interface NetworkStatusModalProps {
  modalShow: boolean;
  handleExitModalClose: () => void;
  networkStatuses: NetworkStatusItem[];
}

type Severity = 'danger' | 'warning' | 'success' | 'default';

const SEVERITY_PALETTE: Record<Severity, { border: string; bg: string; headerBg: string; badge: string }> = {
  danger:  { border: '#dc3545', bg: '#fff8f8', headerBg: '#ffeaea', badge: '#dc3545' },
  warning: { border: '#e67e22', bg: '#fffbf5', headerBg: '#fff3e0', badge: '#e67e22' },
  success: { border: '#198754', bg: '#f5fff9', headerBg: '#e6f9ef', badge: '#198754' },
  default: { border: '#0d6efd', bg: '#f5f8ff', headerBg: '#e8f0ff', badge: '#0d6efd' },
};

function getSeverityPalette(status: string) {
  const sev = getNetworkStatusSeverity(status) as Severity;
  return SEVERITY_PALETTE[sev] ?? SEVERITY_PALETTE.default;
}

interface DetailRowProps {
  label: string;
  value: string;
  preWrap?: boolean;
  valueIsHtml?: boolean;
}

function DetailRow({ label, value, preWrap, valueIsHtml }: DetailRowProps) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '148px 1fr', gap: '0.4rem 0.75rem', fontSize: '0.875rem', lineHeight: 1.55, alignItems: 'start' }}>
      <span style={{ fontWeight: 600, color: '#5a6370', paddingTop: 1 }}>{label}</span>
      <span
        style={{ color: '#1a1f27', whiteSpace: preWrap ? 'pre-wrap' : undefined }}
        {...(valueIsHtml ? { dangerouslySetInnerHTML: { __html: value } } : { children: value })}
      />
    </div>
  );
}

function NetworkStatusModal({ modalShow, handleExitModalClose, networkStatuses }: NetworkStatusModalProps) {
  const formatDate = (value?: string | null, fallback?: string | null) => {
    const dateValue = value || fallback;
    if (!dateValue) return 'Unknown';
    return new Date(dateValue).toLocaleString('en-GB', { timeZone: 'UTC' });
  };

  return (
    <Modal
      aria-labelledby="network-incidents-title"
      centered
      show={modalShow}
      onHide={handleExitModalClose}
      size="lg"
    >
      <Modal.Header closeButton style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e9ecef' }}>
        <Modal.Title id="network-incidents-title" style={{ fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Network Incidents
          {networkStatuses.length > 0 && (
            <span style={{
              backgroundColor: '#6c757d',
              color: '#fff',
              borderRadius: '999px',
              padding: '0.1rem 0.55rem',
              fontSize: '0.72rem',
              fontWeight: 600,
              lineHeight: 1.6,
            }}>
              {networkStatuses.length}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        {networkStatuses.map((networkStatus: NetworkStatusItem) => {
          const palette = getSeverityPalette(networkStatus.status);
          return (
            <div
              key={networkStatus.id}
              style={{
                borderRadius: '8px',
                border: `1px solid ${palette.border}30`,
                borderLeft: `4px solid ${palette.border}`,
                backgroundColor: palette.bg,
                overflow: 'hidden',
              }}
            >
              {/* Incident header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.6rem 1rem',
                backgroundColor: palette.headerBg,
                borderBottom: `1px solid ${palette.border}20`,
                gap: '0.75rem',
              }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1f27', flexShrink: 1, minWidth: 0 }}>
                  {networkStatus.title}
                </span>
                <span style={{
                  flexShrink: 0,
                  backgroundColor: palette.badge,
                  color: '#fff',
                  borderRadius: '999px',
                  padding: '0.18rem 0.7rem',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}>
                  {getNetworkStatusLabel(networkStatus.status)}
                </span>
              </div>

              {/* Detail rows */}
              <div style={{ padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                {networkStatus.networks.length > 0 && (
                  <DetailRow
                    label="Affected Networks"
                    value={networkStatus.networks.map((n) => n.networkName).join(', ')}
                  />
                )}
                {networkStatus.activeStatuses.length > 0 && (
                  <DetailRow
                    label="Active States"
                    value={networkStatus.activeStatuses.map((s) => getNetworkStatusLabel(s)).join(', ')}
                  />
                )}
                <DetailRow
                  label="From"
                  value={`${formatDate(networkStatus.startDate, networkStatus.createdDate)} until ${networkStatus.endDate ? formatDate(networkStatus.endDate) : 'further notice'}`}
                />
                <DetailRow label="Summary" value={networkStatus.summary} preWrap valueIsHtml />
              </div>
            </div>
          );
        })}
      </Modal.Body>

      <Modal.Footer style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #e9ecef' }}>
        <Button variant="secondary" onClick={handleExitModalClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default NetworkStatusModal;
